import { format, parse, startOfDay } from 'date-fns';
import { Building, Laptop, Link, MapPin } from 'lucide-react';
import { type ElementType, type ReactElement } from 'react';
import { SessionStatusBadge } from '@avanta/shared/components/SessionStatusBadge';
import {
  APPOINTMENT_REMOTE_ID,
  AppointmentType,
  ENTERPRISE_APPOINTMENT_STATUS,
  HIDDEN_SESSION_STATUSES,
  SESSION_STATUS,
  type SessionStatus,
} from '@avanta/shared/constants/appointments';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { t } from '@avanta/shared/i18n';
import {
  type AvailableDates,
  type Appointment,
  type AppointmentStatus,
  type AppointmentTypeValue,
  type Center,
  type DailySlot,
  type EnterpriseAppointmentStatus,
  type SessionDetails,
  type TimeSlot,
} from '@avanta/shared/types/appointments';

const ENTERPRISE_TO_SESSION_STATUS: Record<EnterpriseAppointmentStatus, SessionStatus> = {
  [ENTERPRISE_APPOINTMENT_STATUS.PENDING]: SESSION_STATUS.PENDING,
  [ENTERPRISE_APPOINTMENT_STATUS.SCHEDULED]: SESSION_STATUS.SCHEDULED,
  [ENTERPRISE_APPOINTMENT_STATUS.CONFIRMED]: SESSION_STATUS.CONFIRMED,
  [ENTERPRISE_APPOINTMENT_STATUS.DONE]: SESSION_STATUS.DONE,
  [ENTERPRISE_APPOINTMENT_STATUS.COMPLETED]: SESSION_STATUS.COMPLETED,
  [ENTERPRISE_APPOINTMENT_STATUS.CANCELLED]: SESSION_STATUS.CANCELED,
};

export const isEnterpriseAppointmentStatus = (
  status: string
): status is EnterpriseAppointmentStatus =>
  Object.values(ENTERPRISE_APPOINTMENT_STATUS).includes(status as EnterpriseAppointmentStatus);

export const getSessionStatusFromEnterpriseStatus = (
  status: EnterpriseAppointmentStatus
): SessionStatus => ENTERPRISE_TO_SESSION_STATUS[status];

/**
 * Fallback when AppointmentDTO.status is missing (e.g. historical DTOs):
 * canceled / done / completed / confirmed / pending.
 */
export const getSessionStatus = (
  appointment: Pick<AppointmentStatus, 'confirmed' | 'canceled' | 'dateTime' | 'hasReport'>
): SessionStatus => {
  if (appointment.canceled) {
    return SESSION_STATUS.CANCELED;
  }

  if (new Date(appointment.dateTime) <= new Date()) {
    return appointment.hasReport ? SESSION_STATUS.COMPLETED : SESSION_STATUS.DONE;
  }

  if (appointment.confirmed) {
    return SESSION_STATUS.CONFIRMED;
  }

  return SESSION_STATUS.PENDING;
};

export const resolveSessionStatus = (appointment: AppointmentStatus): SessionStatus => {
  if (appointment.status && isEnterpriseAppointmentStatus(appointment.status)) {
    return getSessionStatusFromEnterpriseStatus(appointment.status);
  }

  return getSessionStatus(appointment);
};

export const isUpcomingAppointment = (
  appointment: Pick<Appointment, 'dateTime' | 'canceled'> & {
    sessionDetails?: Pick<SessionDetails, 'type'> &
      Partial<Pick<SessionDetails, 'durationInMinutes'>>;
  }
): boolean => {
  if (HIDDEN_SESSION_STATUSES.has(appointment.sessionDetails?.type ?? '') || appointment.canceled)
    return false;
  const endTime = new Date(appointment.dateTime);
  endTime.setMinutes(endTime.getMinutes() + (appointment.sessionDetails?.durationInMinutes ?? 0));
  return endTime > new Date();
};

export const getSessionStatusLabel = (status: SessionStatus): string => {
  if (status === SESSION_STATUS.CONFIRMED) {
    return t('AppointmentCard.states.confirmed', { ns: LOCIZE_NS.APPOINTMENTS });
  }

  if (status === SESSION_STATUS.CANCELED) {
    return t('AppointmentCard.states.canceled', { ns: LOCIZE_NS.APPOINTMENTS });
  }

  if (status === SESSION_STATUS.DONE) {
    return t('AppointmentCard.states.done', { ns: LOCIZE_NS.APPOINTMENTS });
  }

  if (status === SESSION_STATUS.COMPLETED) {
    return t('AppointmentCard.states.completed', { ns: LOCIZE_NS.APPOINTMENTS });
  }

  if (status === SESSION_STATUS.PENDING) {
    return t('AppointmentCard.states.pending', { ns: LOCIZE_NS.APPOINTMENTS });
  }

  return t('AppointmentCard.states.scheduled', { ns: LOCIZE_NS.APPOINTMENTS });
};

export const getStatusBadge = (
  appointment: AppointmentStatus,
  className?: string
): ReactElement => {
  const status = resolveSessionStatus(appointment);

  return (
    <SessionStatusBadge status={status} className={className}>
      {getSessionStatusLabel(status)}
    </SessionStatusBadge>
  );
};

export const getAppointmentSpecialityLabel = (specialityId: number | string): string =>
  t(`AppointmentPdf.Speciality.options.${specialityId}`, {
    ns: LOCIZE_NS.APPOINTMENTS,
  });

export const mapBookedContactMethod = (
  bookedContactMethod: AppointmentTypeValue,
  backofficeCenterId: number | undefined
): AppointmentTypeValue => {
  if (backofficeCenterId === APPOINTMENT_REMOTE_ID) {
    return AppointmentType.Video;
  }

  if (Object.values(AppointmentType).includes(bookedContactMethod)) {
    return bookedContactMethod;
  }

  return AppointmentType.Video;
};

export const getCenterName = (
  bookedContactMethod: AppointmentTypeValue,
  center: (Center | { name: string; city?: string }) | undefined
): string => {
  if (bookedContactMethod === AppointmentType.InPerson && center) {
    return center.city ? `${center.name} (${center.city})` : center.name;
  }

  if (bookedContactMethod === AppointmentType.Video) {
    return t('AppointmentCard.types.remote', { ns: LOCIZE_NS.APPOINTMENTS });
  }

  return '';
};

export const getPatientName = (
  patient?: {
    firstName?: string;
    surname?: string;
    nickname?: string;
  } | null
): string =>
  patient?.firstName && patient?.surname
    ? `${patient.firstName} ${patient.surname}`
    : (patient?.nickname ?? '');

export const getLocationDetails = (
  appointment: Appointment,
  isOnline: boolean
): { icon: ElementType; title?: string; value?: string; link?: string }[] => {
  if (isOnline) {
    return [
      {
        icon: Laptop,
        title: t('AppointmentCard.location.label', { ns: LOCIZE_NS.APPOINTMENTS }),
        value: t('AppointmentsReserve.FormData.remote.label', { ns: LOCIZE_NS.APPOINTMENTS }),
      },
      {
        icon: Link,
        title: t('AppointmentCard.link.label', { ns: LOCIZE_NS.APPOINTMENTS }),
        value: appointment.sessionDetails?.onlineLink,
        link: appointment.sessionDetails?.onlineLink,
      },
    ];
  }

  return [
    {
      icon: Building,
      title: t('AppointmentCard.center.label', { ns: LOCIZE_NS.APPOINTMENTS }),
      value: appointment.center?.name || '',
    },
    {
      icon: MapPin,
      title: t('AppointmentCard.address.label', { ns: LOCIZE_NS.APPOINTMENTS }),
      value: ` ${appointment.center?.address} 
      ${appointment.center?.city} (${appointment.center?.postalCode})`,
    },
  ];
};

export const checkIsDayDisabled = (
  day: Date,
  availableDates: AvailableDates,
  today: Date,
  endDate: Date
): boolean => {
  const normalizedDay = startOfDay(day);
  const normalizedToday = startOfDay(today);
  const normalizedEndDate = startOfDay(endDate);
  if (normalizedDay < normalizedToday || normalizedDay > normalizedEndDate) return true;

  return !availableDates[format(day, 'yyyy-MM-dd')];
};

export const getAvailableDatesFromDailySlots = (dailySlots: DailySlot[]): AvailableDates =>
  Object.fromEntries(dailySlots.map(slot => [slot.date, [...slot.times].sort()]));

export const getFirstAvailableDate = (availableDates: AvailableDates): Date | undefined => {
  const firstDate = Object.keys(availableDates).sort()[0];
  return firstDate ? parse(firstDate, 'yyyy-MM-dd', new Date()) : undefined;
};

export const centersByCity = <CenterType extends { city: string }>(
  centers: CenterType[]
): Record<string, CenterType[]> =>
  centers.reduce<Record<string, CenterType[]>>((acc, center) => {
    if (!center.city) return acc;

    acc[center.city] ??= [];
    acc[center.city].push(center);
    return acc;
  }, {});

export const getCenterOptions = (
  centers: Center[],
  city: string,
  field: 'city' | 'backofficeCenterId'
): { value: string; label: string; description?: string }[] => {
  const groupedCenters = centersByCity(centers);

  if (field === 'city') {
    return Object.keys(groupedCenters).map(centerCity => ({
      value: centerCity,
      label: centerCity,
    }));
  }

  return (groupedCenters[city] ?? []).map(center => ({
    value: String(center.id),
    label: center.name,
    description: `${center.address} ${center.postalCode} ${center.city}`,
  }));
};

export const DAYTIME_HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];
const ALL_HOURS = Array.from({ length: 24 }, (_, hour) => hour);

const parseSlotHour = (time: string): number => parseInt(time.split(':')[0], 10);

const formatSlotTime = (hour: number): string => `${String(hour).padStart(2, '0')}:00`;

export const getTimeSlotsForDate = (
  date: Date,
  availableDates: AvailableDates,
  hours: number[] = DAYTIME_HOURS
): { timeSlots: TimeSlot[]; allAvailableSlots: TimeSlot[] } => {
  const times = availableDates[format(date, 'yyyy-MM-dd')] ?? [];
  const availableHours = new Set(times.map(parseSlotHour));

  const toTimeSlot = (hour: number): TimeSlot => ({
    time: formatSlotTime(hour),
    disabled: !availableHours.has(hour),
  });

  return {
    timeSlots: hours.map(toTimeSlot),
    allAvailableSlots: ALL_HOURS.map(toTimeSlot),
  };
};
